// LWDEditHome, the production art direction of The LWD Edit cover.
// Not a grid of sections but a paced journey, six moments:
//   Arrival · Recognition · Discovery · Inspiration · (human) · Invitation.
// Three cinematic images carry it (a venue, a wedding, a creatives moment);
// everything else recedes. Product-first: Venues/Creatives/Destinations come
// before the deep editorial. Header is the standard site chrome (DirectoryNav +
// Masthead), unchanged. World tiles are honest wayfinding (image + label + real
// link) that fill as content is published. Scope: this file only.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabaseClient.js';
import { useEditorialLights } from '../Editorial/useEditorialLights.js';
import { usePrerenderReady } from '../../utils/prerenderReady';
import DirectoryNav from '../Editorial/DirectoryNav.jsx';
import { Masthead } from '../Editorial/EditorialHomePage.jsx';
import { isPubliclyRoutable } from '../../utils/publicRoutability.js';
import { imgUrl } from '../../utils/imgUrl';

const FD = "'Gilda Display', 'Playfair Display', Georgia, serif";
const FU = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";
const CDN = id => imgUrl(`https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/listing-media/${id}.jpg`, { width: 1800 });
const FALLBACK = CDN('b1c3cb76-17ef-4e11-9b92-c9ee54e100dd');

const lc = s => (s || '').toLowerCase();
const imageFor = i => imgUrl(i?.cover_image || i?.hero_image_url || i?.hero_image, { width: 1800, resize: 'contain' }) || FALLBACK;
const excerptFor = i => i?.standfirst || i?.excerpt || i?.subtitle || '';
const storyKind = p => { const c = lc(p?.category_slug); return c === 'perspectives' ? 'Essay' : c === 'destinations' ? 'Guide' : 'Story'; };
const readLabel = p => [storyKind(p), p?.reading_time ? `${p.reading_time} min read` : null].filter(Boolean).join('  ·  ');

// The three commercial pillars, product-first Discovery.
const PILLARS = [
  { title: 'Our Venues',    desc: "The world's most extraordinary places to say I do.", slug: 'our-venues',    img: '01ec46ca-ec6b-4d48-ae64-4209c4f73de7' },
  { title: 'Our Creatives', desc: 'The artists whose craft brings weddings to life.',    slug: 'our-creatives', img: '43879c5f-eac1-4634-96ed-71872c982046' },
  { title: 'Destinations',  desc: 'Where in the world to marry, and why.',               slug: 'destinations',  img: '19f527de-8df7-46c6-9d3a-20a062ffc920' },
];

// Lifestyle world, Living (supporting editorial band, page-turn beat).
const LIVING_TILES = [
  { title: 'Hotels',            slug: 'hotels',      img: 'bf5bf27a-74a9-45cf-bf09-5e94dd847c5d' },
  { title: 'Restaurants',       slug: 'restaurants', img: 'def0e434-6cbe-4495-b656-e421eb2440c1' },
  { title: 'Design & Interiors', slug: 'home-living', img: '4249ab8f-e4db-4518-9dd3-bbc79f1aace7' },
  { title: 'Flowers',           slug: 'flowers',     img: '43879c5f-eac1-4634-96ed-71872c982046' },
  { title: 'Entertaining',      slug: 'entertaining', img: 'd70574ee-146e-4ecd-b0da-e16fb5662aa6' },
  { title: 'Wine & Spirits',    slug: 'wine',        img: '1b171019-3011-4ee5-b367-4797602ba2de' },
  { title: 'Art & Culture',     slug: 'art-culture', img: 'c0b90de9-64ea-4b8b-9f39-31ab9f69836d' },
];

const COLL_ICON = {
  'castle-weddings':    <path d="M4 20h16M6 20V9m4 11V9m4 11V9m4 11V9M3 9l9-5 9 5" />,
  'italian-villas':     <path d="M5 20V9l7-5 7 5v11M9 20v-6h6v6" />,
  'coastal-escapes':    <><path d="M3 10c2 0 2 1.5 4.5 1.5S10 10 12 10s2.5 1.5 4.5 1.5S21 10 21 10" /><path d="M3 15c2 0 2 1.5 4.5 1.5S10 15 12 15s2.5 1.5 4.5 1.5S21 15 21 15" /></>,
  'grand-hotels':       <path d="M4 20V6h16v14M8 20V6m8 14V6M4 10h16" />,
  'historic-estates':   <path d="M4 20h16M6 20V10m12 10V10M4 10l8-5 8 5M10 20v-5h4v5" />,
  'editors-picks':      <path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-.5z" />,
  'recently-published': <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
};

function A({ href, onClick, className, style, children }) {
  return <a href={href} className={className} style={style} onClick={e => { if (onClick) { e.preventDefault(); onClick(); } }}>{children}</a>;
}

export default function LWDEditHome({ onNavigateArticle, onNavigateCategory, onNavigateHome }) {
  const [isDark, , toggleLights] = useEditorialLights();
  const [posts, setPosts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prerender readiness (P1b follow-up, 2026-07-24): loading gates both posts and
  // collections (single Promise.all below); the cover-story H1 comes from posts[0], so
  // this is the correct settle signal for the page's SEO-critical content.
  usePrerenderReady(!loading);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const [postsRes, collRes] = await Promise.all([
        supabase.from('magazine_posts')
          .select('id, slug, title, standfirst, excerpt, cover_image, category_slug, category_label, tags, editors_choice, editorial_priority, published_at, reading_time')
          .order('editors_choice', { ascending: false })
          .order('editorial_priority', { ascending: true, nullsFirst: false })
          .order('published_at', { ascending: false })
          .limit(80),
        supabase.from('editorial_collections')
          .select('slug, title, hero_image_url, filter_rules, published').eq('published', true),
      ]);
      if (!alive) return;
      if (!postsRes.error) setPosts(postsRes.data || []);
      if (!collRes.error) setCollections(collRes.data || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const T = isDark ? {
    BG: '#0a0a0a', PANEL: '#0d0d0b', BAND: '#111', TEXT: '#f5f0e8', MUTED: 'rgba(245,240,232,0.6)', FAINT: 'rgba(245,240,232,0.42)', LINE: 'rgba(245,240,232,0.12)', GOLD: '#c9a96e',
  } : {
    BG: '#f6f2ea', PANEL: '#fffdf8', BAND: '#efe9dd', TEXT: '#1b1710', MUTED: 'rgba(27,23,16,0.62)', FAINT: 'rgba(27,23,16,0.44)', LINE: 'rgba(27,23,16,0.12)', GOLD: '#a9823f',
  };

  const picks = useMemo(() => {
    const chosen = posts.filter(p => p.editors_choice === true);
    const seen = new Set(chosen);
    const ranked = posts.filter(p => !seen.has(p) && p.editorial_priority != null).sort((a, b) => a.editorial_priority - b.editorial_priority);
    ranked.forEach(p => seen.add(p));
    return [...chosen, ...ranked, ...posts.filter(p => !seen.has(p))];
  }, [posts]);

  const cover = picks[0] || posts[0] || null;
  const pickRow = picks.slice(1, 6);

  const featuredCollections = useMemo(() => collections
    .filter(c => lc(c.filter_rules?.category_slug || 'our-venues') === 'our-venues')
    .sort((a, b) => (a.filter_rules?.display_order ?? 99) - (b.filter_rules?.display_order ?? 99))
    .slice(0, 7), [collections]);

  const goArticle = slug => onNavigateArticle && onNavigateArticle(slug);
  const goCat = slug => onNavigateCategory && onNavigateCategory(slug);

  return (
    <main style={{ background: T.BG, color: T.TEXT }}>
      <style>{`
        .lwd-img { transition: transform 0.9s cubic-bezier(0.16,1,0.3,1); }
        .lwd-card:hover .lwd-img { transform: scale(1.05); }
        .lwd-card { transition: transform 0.32s cubic-bezier(0.16,1,0.3,1); }
        .lwd-card:hover { transform: translateY(-4px); }
        .lwd-hscroll { scrollbar-width: none; -ms-overflow-style: none; }
        .lwd-hscroll::-webkit-scrollbar { display: none; }
        .lwd-viewall { transition: gap 0.2s ease; } .lwd-viewall:hover { gap: 12px; }
        .lwd-coll-cell { transition: background 0.2s ease; } .lwd-coll-cell:hover { background: ${T.GOLD}12; }
        @keyframes lwdRise { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
        .lwd-rise { animation: lwdRise 1s cubic-bezier(0.16,1,0.3,1) both; }
        @media (max-width: 960px) {
          .lwd-picks { grid-template-columns: 1fr 1fr !important; }
          .lwd-pillars { grid-template-columns: 1fr !important; }
          .lwd-style { grid-template-columns: 1fr 1fr !important; }
          .lwd-collrow { grid-template-columns: repeat(4, 1fr) !important; }
          .lwd-recognition { font-size: 34px !important; }
        }
        @media (max-width: 640px) {
          .lwd-cover-title { font-size: clamp(30px, 8vw, 40px) !important; -webkit-line-clamp: 3 !important; margin-bottom: 16px !important; }
          .lwd-cover-stan  { -webkit-line-clamp: 2 !important; font-size: 14px !important; margin-bottom: 24px !important; }
        }
      `}</style>

      <DirectoryNav onNavigateHome={onNavigateHome} />
      <Masthead activeId={null} onNavigateHome={onNavigateHome} onNavigateCategory={onNavigateCategory} isDark={isDark} onToggleLights={toggleLights} />

      {loading ? (
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: FU, fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: T.FAINT }}>Loading The Edit</span>
        </div>
      ) : (
        <>
          {/* 1 · ARRIVAL, one extraordinary image */}
          {cover && <CoverStory post={cover} T={T} onOpen={() => goArticle(cover.slug)} />}

          {/* 2 · RECOGNITION, a single belief, room to breathe */}
          <Recognition T={T} />

          {/* 3 · DISCOVERY, product first */}
          <Discovery T={T} goCat={goCat} />

          {/* 4 · INSPIRATION, the publication begins */}
          {pickRow.length >= 3 && <EditorsPicks lead={pickRow[0]} rest={pickRow.slice(1)} T={T} goArticle={goArticle} goCat={goCat} />}

          {/* image II, a wedding */}
          <CinematicMoment T={T} img="b6aeb856-83e5-430c-b1c0-a3e25e0eeb69" eyebrow="Real Weddings"
            line="Love stories, told in full." cta="Explore Real Weddings" onCta={() => goCat('real-weddings')} align="left" />

          {featuredCollections.length >= 3 && <Collections items={featuredCollections} T={T} goCat={goCat} />}

          <WorldBand id="living" title="Living Beautifully" tiles={LIVING_TILES} T={T} goCat={goCat} viewAll="home-living" variant="living" />

          {/* image III, the craft / people */}
          <CinematicMoment T={T} img="0d8da969-9f13-47f1-ab2a-026f6bd1b306" eyebrow="The People"
            line="Behind every unforgettable day, an artist." cta="Meet Our Creatives" onCta={() => goCat('our-creatives')} align="right" />

          {/* 6 · INVITATION */}
          <EditorialDesk T={T} />
        </>
      )}
    </main>
  );
}

// ── 1 · Arrival, cover ───────────────────────────────────────────────────────
function CoverStory({ post, T, onOpen }) {
  return (
    <section style={{ position: 'relative', height: 'clamp(600px, 88svh, 900px)', overflow: 'hidden', background: '#111' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imageFor(post)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.36) 42%, rgba(0,0,0,0.02) 72%), linear-gradient(to top, rgba(0,0,0,0.5), transparent 38%)' }} />
      <div style={{ position: 'relative', height: '100%', maxWidth: 1320, margin: '0 auto', padding: '0 clamp(28px, 7vw, 104px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: 'clamp(60px, 9vw, 120px)' }}>
        <div className="lwd-rise" style={{ maxWidth: 640 }}>
          <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 22 }}>Cover Story</div>
          <h1 className="lwd-cover-title" style={{ fontFamily: FD, fontSize: 'clamp(38px, 4.4vw, 58px)', fontWeight: 400, color: '#f7f2ea', margin: '0 0 22px', lineHeight: 1.06, letterSpacing: '-0.015em', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.title}</h1>
          {excerptFor(post) && <p className="lwd-cover-stan" style={{ fontFamily: FU, fontSize: 'clamp(15px, 1.4vw, 17px)', fontWeight: 300, color: 'rgba(247,242,234,0.86)', margin: '0 0 30px', lineHeight: 1.7, maxWidth: 470, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{excerptFor(post)}</p>}
          <button onClick={onOpen} style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f7f2ea', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(247,242,234,0.5)', padding: '16px 34px', cursor: 'pointer', backdropFilter: 'blur(3px)', transition: 'all 0.22s' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.GOLD; e.currentTarget.style.borderColor = T.GOLD; e.currentTarget.style.color = '#1b1710'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(247,242,234,0.5)'; e.currentTarget.style.color = '#f7f2ea'; }}>
            Read the Story →
          </button>
        </div>
      </div>
    </section>
  );
}

// ── 2 · Recognition, the pause ───────────────────────────────────────────────
function Recognition({ T }) {
  return (
    <section style={{ background: T.BG, padding: 'clamp(90px, 13vw, 200px) clamp(24px, 6vw, 104px)' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 30 }}>The World of Luxury Weddings</div>
        <h2 className="lwd-recognition" style={{ fontFamily: FD, fontSize: 'clamp(34px, 5.2vw, 76px)', fontWeight: 400, color: T.TEXT, margin: 0, lineHeight: 1.08, letterSpacing: '-0.015em', maxWidth: 16 + 'ch' }}>
            Every remarkable wedding begins with a remarkable place.
        </h2>
      </div>
    </section>
  );
}

// ── 3 · Discovery, the three pillars ─────────────────────────────────────────
function Discovery({ T, goCat }) {
  return (
    <section id="world-places" style={{ background: T.BG, padding: '0 clamp(24px, 5vw, 80px) clamp(64px, 7vw, 100px)', scrollMarginTop: 80 }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <div className="lwd-pillars" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'clamp(14px, 1.6vw, 22px)' }}>
          {PILLARS.map(p => (
            <A key={p.slug} href={`/lwd-edit/category/${p.slug}`} onClick={() => goCat(p.slug)} className="lwd-card" style={{ position: 'relative', display: 'block', aspectRatio: '3 / 3.7', overflow: 'hidden', borderRadius: 3, textDecoration: 'none' }}>
              <div className="lwd-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${CDN(p.img)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.66) 0%, rgba(0,0,0,0) 52%)' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(22px, 2.2vw, 34px)' }}>
                <div style={{ fontFamily: FD, fontSize: 'clamp(24px, 2.2vw, 34px)', fontWeight: 400, color: '#f7f2ea', lineHeight: 1.06, marginBottom: 9 }}>{p.title}</div>
                <div style={{ fontFamily: FU, fontSize: 13, fontWeight: 300, color: 'rgba(247,242,234,0.76)', lineHeight: 1.55, marginBottom: 14, maxWidth: 260 }}>{p.desc}</div>
                <span className="lwd-viewall" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FU, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD }}>Explore {p.title.replace('Our ', '')} →</span>
              </div>
            </A>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Editor's Picks ────────────────────────────────────────────────────────────
function EditorsPicks({ lead, rest, T, goArticle, goCat }) {
  return (
    <section style={{ background: T.BG, padding: 'clamp(20px, 2vw, 32px) clamp(24px, 5vw, 80px) clamp(60px, 6.5vw, 92px)' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <SectionHead title="Editor's Picks" cta="View All Editor's Picks" onCta={() => goCat('our-venues')} T={T} />
        <div className="lwd-picks" style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 'clamp(14px, 1.8vw, 24px)', alignItems: 'stretch' }}>
          <PickCard post={lead} T={T} lead onGo={() => goArticle(lead.slug)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 'clamp(12px, 1.4vw, 18px)' }}>
            {rest.slice(0, 4).map((p, i) => <PickCard key={p.id || i} post={p} T={T} onGo={() => goArticle(p.slug)} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
function PickCard({ post, T, lead, onGo }) {
  return (
    <A href={`/lwd-edit/${post.slug}`} onClick={onGo} className="lwd-card" style={{ position: 'relative', display: 'block', ...(lead ? { aspectRatio: '4 / 3.4' } : { height: '100%', minHeight: 150 }), overflow: 'hidden', borderRadius: 3, textDecoration: 'none' }}>
      <div className="lwd-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imageFor(post)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.04) 62%)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: lead ? 'clamp(20px, 2vw, 30px)' : 'clamp(13px, 1.3vw, 17px)' }}>
        <div style={{ fontFamily: FU, fontSize: lead ? 9.5 : 8, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 7 }}>{lead ? 'Featured' : (post.category_label || post.category_slug || 'The Edit')}</div>
        <h3 style={{ fontFamily: FD, fontSize: lead ? 'clamp(21px, 2vw, 27px)' : 'clamp(14px, 1.2vw, 17px)', fontWeight: 400, color: '#f7f2ea', margin: '0 0 8px', lineHeight: 1.16 }}>{post.title}</h3>
        <div style={{ fontFamily: FU, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(247,242,234,0.6)' }}>{readLabel(post)}</div>
      </div>
    </A>
  );
}

// ── Cinematic full-bleed moment (images II & III) ─────────────────────────────
function CinematicMoment({ T, img, eyebrow, line, cta, onCta, align = 'left' }) {
  const left = align === 'left';
  return (
    <section style={{ position: 'relative', height: 'clamp(480px, 74svh, 780px)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${CDN(img)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div style={{ position: 'absolute', inset: 0, background: left ? 'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.02) 100%)' : 'linear-gradient(270deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.02) 100%)' }} />
      <div style={{ position: 'relative', height: '100%', maxWidth: 1320, margin: '0 auto', padding: '0 clamp(28px, 7vw, 104px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: left ? 'flex-start' : 'flex-end', textAlign: left ? 'left' : 'right' }}>
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 20 }}>{eyebrow}</div>
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(30px, 4vw, 56px)', fontWeight: 400, color: '#f7f2ea', margin: '0 0 28px', lineHeight: 1.06, letterSpacing: '-0.01em' }}>{line}</h2>
          <button onClick={onCta} style={{ fontFamily: FU, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f7f2ea', background: 'none', border: '1px solid rgba(247,242,234,0.5)', padding: '14px 30px', cursor: 'pointer', transition: 'all 0.22s' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.GOLD; e.currentTarget.style.borderColor = T.GOLD; e.currentTarget.style.color = '#1b1710'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'rgba(247,242,234,0.5)'; e.currentTarget.style.color = '#f7f2ea'; }}>
            {cta} →
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Explore Collections (quiet index) ─────────────────────────────────────────
function Collections({ items, T, goCat }) {
  return (
    <section style={{ background: T.BG, padding: 'clamp(60px, 6.5vw, 100px) 0' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(24px, 5vw, 80px)' }}>
        <SectionHead title="Explore Collections" cta="View All Collections" onCta={() => goCat('our-venues')} T={T} />
      </div>
      <div className="lwd-hscroll" style={{ display: 'flex', gap: 'clamp(12px, 1.4vw, 18px)', overflowX: 'auto', padding: '0 clamp(24px, 5vw, 80px)', scrollSnapType: 'x mandatory' }}>
        {items.map(c => (
          <a key={c.slug} className="lwd-card lwd-coll" href={`/lwd-edit/category/${c.filter_rules?.category_slug || 'our-venues'}?collection=${c.slug}`}
            style={{ position: 'relative', flex: '0 0 auto', width: 'clamp(160px, 16vw, 210px)', aspectRatio: '3 / 4', overflow: 'hidden', borderRadius: 3, textDecoration: 'none', scrollSnapAlign: 'start' }}>
            <div className="lwd-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imgUrl(c.hero_image_url, { width: 800 }) || FALLBACK})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.04) 62%)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(14px, 1.4vw, 20px)' }}>
              <div style={{ fontFamily: FD, fontSize: 'clamp(15px, 1.3vw, 19px)', fontWeight: 400, color: '#f7f2ea', lineHeight: 1.14 }}>{c.title}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ── Lifestyle world band (Style / Living) ─────────────────────────────────────
function WorldBand({ id, title, tiles: rawTiles, T, goCat, viewAll, variant }) {
  const style = variant === 'style';
  // These bands are hardcoded editorial furniture. Every one of the seven "Living"
  // tiles (hotels, restaurants, home-living, flowers, entertaining, wine,
  // art-culture) pointed at a category that does not exist as a row at all, so the
  // Edit index was advertising seven sections and answering 404 on each. They were
  // the last 7 internal 404s on the site.
  //
  // Gated rather than deleted: the day one of these categories is genuinely created
  // and populated, its tile reappears on its own. And a band with nothing routable
  // left is not rendered, because a section header over an empty shelf is worse than
  // no section. The "View All" CTA is held to the same test.
  const tiles = (rawTiles || []).filter(t => isPubliclyRoutable(`/lwd-edit/category/${t.slug}`));
  const showViewAll = viewAll && isPubliclyRoutable(`/lwd-edit/category/${viewAll}`);
  if (!tiles.length) return null;
  return (
    <section id={`world-${id}`} style={{ background: T.BG, padding: 'clamp(16px, 1.8vw, 26px) clamp(24px, 5vw, 80px) clamp(40px, 4.5vw, 64px)', scrollMarginTop: 80 }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <SectionHead title={title} cta={showViewAll ? 'View All' : null} onCta={showViewAll ? () => goCat(viewAll) : undefined} T={T} small />
      </div>
      {style ? (
        <div className="lwd-style" style={{ maxWidth: 1320, margin: '0 auto', display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: 'clamp(12px, 1.4vw, 18px)' }}>
          {tiles.map(t => <MiniTile key={t.slug + t.title} tile={t} T={T} goCat={goCat} aspect="5 / 6" />)}
        </div>
      ) : (
        <div className="lwd-hscroll" style={{ display: 'flex', gap: 'clamp(12px, 1.4vw, 18px)', overflowX: 'auto', padding: '0 clamp(24px, 5vw, 80px)', scrollSnapType: 'x mandatory' }}>
          {tiles.map(t => (
            <div key={t.slug + t.title} style={{ flex: '0 0 auto', width: 'clamp(160px, 16vw, 200px)', scrollSnapAlign: 'start' }}>
              <MiniTile tile={t} T={T} goCat={goCat} aspect="1 / 1" />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
function MiniTile({ tile, T, goCat, aspect }) {
  return (
    <A href={`/lwd-edit/category/${tile.slug}`} onClick={() => goCat(tile.slug)} className="lwd-card" style={{ position: 'relative', display: 'block', aspectRatio: aspect, overflow: 'hidden', borderRadius: 3, textDecoration: 'none' }}>
      <div className="lwd-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${CDN(tile.img)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.04) 66%)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(13px, 1.3vw, 18px)' }}>
        <div style={{ fontFamily: FD, fontSize: 'clamp(15px, 1.3vw, 19px)', fontWeight: 400, color: '#f7f2ea', lineHeight: 1.12 }}>{tile.title}</div>
      </div>
    </A>
  );
}

// ── 6 · Invitation, editorial desk ───────────────────────────────────────────
function EditorialDesk({ T }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  return (
    <section style={{ background: T.BG, padding: 'clamp(40px, 4vw, 64px) clamp(24px, 5vw, 80px) clamp(64px, 7vw, 100px)' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', background: T.PANEL, border: `1px solid ${T.LINE}`, borderRadius: 4, display: 'grid', gridTemplateColumns: '200px 1fr 1fr', gap: 'clamp(24px, 3vw, 44px)', padding: 'clamp(28px, 3.4vw, 48px)', alignItems: 'center' }} className="lwd-style">
        <div style={{ aspectRatio: '3 / 4', borderRadius: 3, overflow: 'hidden', background: '#141210', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontFamily: FD, fontSize: 34, color: T.GOLD, letterSpacing: '0.08em' }}>LWD</div><div style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.5)', marginTop: 6 }}>The Edit</div></div>
        </div>
        <div>
          <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 12 }}>From the Editorial Desk</div>
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(24px, 2.4vw, 32px)', fontWeight: 400, color: T.TEXT, margin: '0 0 14px', lineHeight: 1.1 }}>A note from the editor</h2>
          <p style={{ fontFamily: FU, fontSize: 14, fontWeight: 300, color: T.MUTED, margin: '0 0 16px', lineHeight: 1.75 }}>At The LWD Edit, we believe in the power of beauty, storytelling and connection. Every week we share inspiration for your journey, and celebrate the artists and places that make it unforgettable.</p>
          <div style={{ fontFamily: FD, fontSize: 18, color: T.TEXT, fontStyle: 'italic' }}>Taiwo Adedayo</div>
          <div style={{ fontFamily: FU, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT, marginTop: 4 }}>Founder, The LWD Edit</div>
        </div>
        <div>
          <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 12 }}>Stay Inspired</div>
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(20px, 2vw, 26px)', fontWeight: 400, color: T.TEXT, margin: '0 0 18px', lineHeight: 1.15 }}>Get the latest stories from The LWD Edit</h2>
          {done ? <div style={{ fontFamily: FU, fontSize: 13, color: T.GOLD }}>Thank you, you're on the list.</div> : (
            <form onSubmit={e => { e.preventDefault(); if (email.includes('@')) setDone(true); }} style={{ display: 'flex', border: `1px solid ${T.LINE}`, borderRadius: 2, overflow: 'hidden', maxWidth: 360 }}>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Your email address" style={{ flex: 1, minWidth: 0, padding: '12px 14px', border: 'none', outline: 'none', background: T.BG, color: T.TEXT, fontFamily: FU, fontSize: 13 }} />
              <button type="submit" style={{ padding: '12px 22px', border: 'none', background: T.GOLD, color: T.PANEL, fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer' }}>Subscribe</button>
            </form>
          )}
          <div style={{ fontFamily: FU, fontSize: 11, color: T.FAINT, marginTop: 10 }}>No spam. Unsubscribe anytime.</div>
        </div>
      </div>
    </section>
  );
}

// ── Shared section header ─────────────────────────────────────────────────────
function SectionHead({ title, cta, onCta, T, small }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: small ? 'clamp(16px, 1.8vw, 24px)' : 'clamp(22px, 2.4vw, 34px)', flexWrap: 'wrap' }}>
      <h2 style={{ fontFamily: FD, fontSize: small ? 'clamp(19px, 2vw, 26px)' : 'clamp(24px, 2.8vw, 34px)', fontWeight: 400, color: T.TEXT, margin: 0 }}>{title}</h2>
      {cta && <button onClick={onCta} className="lwd-viewall" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD, padding: 0 }}>{cta} →</button>}
    </div>
  );
}
